import { listenDatawrapperResize } from "@/lib/datawrapper-resize";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { InstagramEmbed } from "@/components/InstagramEmbed";

export const Route = createFileRoute("/projets")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:type", content: "website" },
      { title: "Projets, brouillons et ratures — Mathys Gautier" },
      {
        name: "description",
        content:
          "Les productions de Mathys Gautier réalisées hors des rédactions : exercices de cours, expérimentations et projets personnels jamais publiés dans un média.",
      },
      { name: "robots", content: "noimageindex" },
      { property: "og:title", content: "Projets, brouillons et ratures — Mathys Gautier" },

      {
        property: "og:description",
        content:
          "Les productions de Mathys Gautier réalisées hors des rédactions : exercices de cours, expérimentations et projets personnels jamais publiés dans un média.",
      },
      { property: "og:url", content: "https://mathysgautier.com/projets" },
    ],
    links: [{ rel: "canonical", href: "https://mathysgautier.com/projets" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Projets, brouillons et ratures — Mathys Gautier",
          description: "Les productions de Mathys Gautier réalisées hors des rédactions : exercices de cours, expérimentations et projets personnels jamais publiés dans un média.",
          url: "https://mathysgautier.com/projets",
          isPartOf: {
            "@type": "WebSite",
            name: "Mathys Gautier",
            url: "https://mathysgautier.com",
          },
          about: {
            "@type": "Person",
            name: "Mathys Gautier",
            jobTitle: "Journaliste",
            url: "https://mathysgautier.com",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Accueil",
              item: "https://mathysgautier.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Projets",
              item: "https://mathysgautier.com/projets",
            },
          ],
        }),
      },
    ],
  }),
  loader: async ({ context }) => {
    const projets = await context.queryClient.ensureQueryData(projetsQueryOptions);
    // Onglet désactivé tant qu'aucun projet n'est publié.
    if (projets.length === 0) throw notFound();
  },
  errorComponent: ({ error }) => (
    <div role="alert" className="mx-auto max-w-5xl px-6 py-24 text-sm">
      {error instanceof Error ? error.message : "Une erreur est survenue."}
    </div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-5xl px-6 py-24 text-sm">Contenu introuvable.</div>
  ),
  component: Projets,
});

interface Chart {
  url: string;
  title?: string;
  aria_label?: string;
  ariaLabel?: string;
  height?: number;
  caption?: string;
}

interface Projet {
  id: string;
  title: string;
  theme: string | null;
  description: string | null;
  visualization_type: string;
  instagram_url: string | null;
  chart: Chart | null;
}

const projetsQueryOptions = queryOptions({
  queryKey: ["projets"],
  queryFn: async (): Promise<Projet[]> => {
    const { data, error } = await supabase
      .from("projets")
      .select("id,title,theme,description,visualization_type,instagram_url,chart")
      .eq("is_published", true)
      .order("display_order", { ascending: true, nullsFirst: false })
      .order("created_at", { ascending: true });
    if (error) throw error;
    return (data ?? []).map((r) => ({
      id: r.id,
      title: r.title,
      theme: r.theme,
      description: r.description,
      visualization_type: r.visualization_type,
      instagram_url: r.instagram_url,
      chart: (r.chart ?? null) as unknown as Chart | null,
    }));
  },
});

function Projets() {
  const { data: projets } = useSuspenseQuery(projetsQueryOptions);

  useEffect(() => listenDatawrapperResize(), []);

  return (
    <section className="mx-auto max-w-5xl px-6 pt-16 pb-24">
      <div className="relative overflow-hidden rounded-3xl border border-border bg-background">
        <div className="absolute inset-x-0 top-0 h-px bg-ocean" aria-hidden />
        <div className="absolute inset-x-0 bottom-0 h-px bg-ocean" aria-hidden />
        <header className="max-w-3xl px-8 pt-16 pb-10 sm:px-12 sm:pt-20 sm:pb-12">
          <p className="eyebrow text-ocean mb-12">L’envers du décor</p>
          <h1 className="mt-12 font-display text-5xl leading-[1.05] sm:text-6xl">
            Projets, <em className="text-ocean">brouillons</em> &amp; ratures
          </h1>
          <p className="mt-16 max-w-2xl text-base leading-relaxed text-muted-foreground">
            Sont rassemblées ici mes productions réalisées en dehors des rédactions&nbsp;: exercices
            de cours, expérimentations et projets plus personnels — jamais publiés dans un média —
            mais assez révélatrices de mon ambition et de mon approche !
          </p>
        </header>
      </div>

      {projets.length === 0 ? (
        <div className="mt-12 border-t border-border pt-16">
          <p className="eyebrow">Bientôt en ligne</p>
          <h2 className="mt-4 max-w-xl font-serif text-3xl leading-snug">
            La sélection est en cours de préparation.
          </h2>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground">
            Revenez prochainement : cette page accueillera petit à petit mes projets universitaires
            et mes tentatives encore inabouties.
          </p>
        </div>
      ) : (
        projets.map((projet) => (
          <div
            key={projet.id}
            className="mt-10 rounded-3xl border border-border bg-paper p-6 sm:p-10 shadow-[0_10px_40px_-24px_rgba(28,42,63,0.25)]"
          >
            {projet.theme && <p className="eyebrow text-ocean">{projet.theme.toUpperCase()}</p>}
            <h2 className="mt-4 font-serif text-3xl leading-tight sm:text-4xl">{projet.title}</h2>
            {projet.description && (
              <p className="mt-6 max-w-3xl text-muted-foreground whitespace-pre-line">
                {projet.description}
              </p>
            )}

            {projet.visualization_type === "instagram" && projet.instagram_url && (
              <div className="mt-10">
                <InstagramEmbed url={projet.instagram_url} title={projet.title} />
              </div>
            )}

            {projet.visualization_type !== "instagram" && projet.chart?.url && (
              <figure className="liquid-glass mt-10 rounded-2xl p-3">
                <iframe
                  sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms"
                  title={projet.chart.title ?? projet.title}
                  aria-label={
                    projet.chart.aria_label ??
                    projet.chart.ariaLabel ??
                    projet.chart.title ??
                    projet.title
                  }
                  src={projet.chart.url}
                  scrolling="no"
                  loading="lazy"
                  frameBorder="0"
                  style={{ width: 0, minWidth: "100%", border: "none" }}
                  height={projet.chart.height ?? 420}
                  data-external="1"
                />
                {projet.chart.caption && (
                  <figcaption className="mt-2 px-2 text-xs text-muted-foreground">
                    {projet.chart.caption}
                  </figcaption>
                )}
              </figure>
            )}
          </div>
        ))
      )}
    </section>
  );
}
